import { ref, deleteObject, getDownloadURL } from 'firebase/storage'

import { storage } from '../../../firebase'

/**
 * Fetch a GIF file from Firebase Storage and convert it to a File object
 * @param gifUrl - The storage path (e.g., 'gifs/filename.gif')
 * @param filename - Optional filename for the File object
 * @returns Promise<File> - The GIF file as a File object
 */
export const fetchGifAsFile = async (
  gifUrl: string,
  filename?: string
): Promise<File> => {
  try {
    // Get the download URL
    const storageRef = ref(storage, gifUrl)
    const downloadUrl = await getDownloadURL(storageRef)

    // Handle emulator URL correction
    const correctedUrl = downloadUrl.includes('127.0.0.1:9199')
      ? downloadUrl.replace('127.0.0.1:9199', 'localhost:9199')
      : downloadUrl

    // Fetch the GIF from the URL
    const response = await fetch(correctedUrl)

    if (!response.ok) {
      throw new Error(
        `Failed to fetch GIF: ${response.status} ${response.statusText}`
      )
    }

    // Get the blob from the response
    const blob = await response.blob()

    // Determine filename
    let finalFilename: string
    if (filename) {
      finalFilename = filename
    } else {
      // Extract filename from gifUrl path
      const pathParts = gifUrl.split('/')
      const lastPart = pathParts[pathParts.length - 1]
      finalFilename = lastPart.includes('.') ? lastPart : 'gif.gif'
    }

    // Convert to File object
    const file = new File([blob], finalFilename, {
      type: blob.type || 'image/gif',
    })

    return file
  } catch (error) {
    console.error('Error fetching GIF as file:', error)
    throw new Error(
      `Failed to fetch GIF file: ${error instanceof Error ? error.message : 'Unknown error'}`
    )
  }
}

// Copy URL to clipboard
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (error) {
    console.error('Failed to copy to clipboard:', error)
    return false
  }
}

// Download a GIF from a URL
export const downloadGifFromUrl = async (
  url: string,
  filename?: string
): Promise<void> => {
  try {
    // Fetch the GIF from the URL
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(
        `Failed to fetch GIF: ${response.status} ${response.statusText}`
      )
    }

    // Get the blob from the response
    const blob = await response.blob()

    // Create a download link
    const downloadUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl

    // Set the filename - use provided filename or extract from URL
    if (filename) {
      link.download = filename
    } else {
      // Try to extract filename from URL or use a default
      const urlParts = url.split('/')
      const lastPart = urlParts[urlParts.length - 1]
      const filenameFromUrl = lastPart.split('?')[0] // Remove query params
      link.download = filenameFromUrl.includes('.')
        ? filenameFromUrl
        : 'gif.gif'
    }

    // Trigger the download
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the object URL
    URL.revokeObjectURL(downloadUrl)
  } catch (error) {
    console.error('Error downloading GIF:', error)
    throw error
  }
}

// Delete a gif file from Firebase Storage
export const deleteGifFile = async (path: string): Promise<void> => {
  try {
    const storageRef = ref(storage, path)
    await deleteObject(storageRef)
  } catch (error) {
    console.error('Error deleting gif file:', error)
    throw error
  }
}
